// Collection.jsx - Collection and sessions management
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/auth";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { gamesService } from "../../services/games";
import { useLetsPlayModal } from "../../hooks/useLetsPlayModal";
import { useCurateBridge } from "../../hooks/useCurateBridge";
import { useGamePreviewHandlers } from "../../hooks/useGamePreviewHandlers";
import { useCollectionGames } from "../../hooks/useCollectionGames";
import { useCollectionModals } from "../../hooks/useCollectionModals";
import { useCollectionSharing } from "../../hooks/useCollectionSharing";
import { useCollectionFeatures } from "../../hooks/useCollectionFeatures";
import { useBulkOnlineAvailability } from "../../hooks/useBulkOnlineAvailability";
import { usePlayList } from "../../hooks/usePlayList";
import GamePreviewModal from "./GamePreviewModal";
import PlayListSidebar from "./PlayListSidebar";
import { isFirefox } from "../../utils/browserDetection";
import ShareWarningBanner from "../common/ShareWarningBanner";
import "../../styles/share-warning.css";
// import CollectionTileWithOnline from "./CollectionTileWithOnline"; // Replaced by CollectionTileWithBulkOnline
import CollectionTileWithBulkOnline from "../common/CollectionTileWithBulkOnline";
import CollectionHeader from "./CollectionHeader";
import CollectionControls from "./CollectionControls";
import CollectionTabs from "./CollectionTabs";
import SharedCollectionTab from "./SharedCollectionTab";
import CollectionEmptyState from "./CollectionEmptyState";
import CurateSimplified from "./CurateSimplified";
import Marketplace from "./Marketplace";
import InsightCarousel from "./InsightCarousel";
import SimplifyReminderModal from "./SimplifyReminderModal";
import AddGameModal from "./AddGameModal";
import LetsPlayModal from "./LetsPlayModal";
import { SUCCESS_ICON, NOTIFICATION_ICON } from "../../constants/icons";
import { List } from "lucide-react";

// Import styles
import "../../styles/profile-fixed.css";
import "../../styles/profile-responsive-enhanced.css";
import "../../styles/profile-dark-mode.css";
import "../../styles/profile-components.css";
import logger from '../../utils/logger';
import { getGameOwnershipStatus } from "../../utils/gameOwnership";

// Simple debounce implementation
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

function Collection({ user }) {
  const { trackClick } = useAnalytics();
  const navigate = useNavigate();

  // Curate bridge for explicit callback contract
  const { setCurateRef, showSimilarGames, showComparison, resetCurate, setInput } = useCurateBridge();

  // Collection hooks for state management
  const {
    // Tab management
    activeTab,
    setActiveTab,
    initializeTabData,
    // Legacy collection data (backward compatibility)
    collection,
    setCollection,
    loading,
    setLoading,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    toggleSortOrder,
    collectionSearchQuery,
    setCollectionSearchQuery,
    filterMode,
    setFilterMode,
    displayedItems,
    setDisplayedItems,
    itemsPerLoad,
    hasMore,
    setHasMore,
    isLoadingMore,
    setIsLoadingMore,
    lastPlayedData,
    setLastPlayedData
  } = useCollectionGames();

  // Bulk online availability checking
  const gameIds = useMemo(() =>
    displayedItems
      .map(item => item.game_id || item.id)
      .filter(Boolean),
    [displayedItems]
  );
  const { getGameAvailability } = useBulkOnlineAvailability(gameIds);

  const getGameOwnershipStatusForGame = useCallback(
    (gameId) => getGameOwnershipStatus(collection, gameId),
    [collection]
  );

  // State for "To Be Played" pill filter
  const [showToBePlayedOnly, setShowToBePlayedOnly] = useState(false);

  // Play List state
  const {
    playList,
    count: playListCount,
    loading: playListLoading,
    addToPlayList,
    removeFromPlayList,
    clearPlayList,
    isInPlayList,
  } = usePlayList(user);
  const [showPlayListSidebar, setShowPlayListSidebar] = useState(false);
  const [sharedCollectionsLoaded, setSharedCollectionsLoaded] = useState(false);

  const {
    showShareModal,
    setShowShareModal,
    shareUrl,
    setShareUrl,
    showAddGameModal,
    setShowAddGameModal,
    addGameData,
    setAddGameData,
    previewGame,
    setPreviewGame,
    loadingPreview,
    setLoadingPreview,
    marketplaceGame,
    setMarketplaceGame,
    reminderGame,
    setReminderGame,
    showGameSelectionBanner,
    bannerMessage,
    showSuccessBanner,
    successBannerMessage,
    showTransientBanner,
    showSuccessBannerMessage,
    showNotification,
    confirmDialog,
    openConfirmDialog,
    setConfirmDialog,
    notification,
    showCurateSidebar,
    setShowCurateSidebar
  } = useCollectionModals();

  const {
    sharedCollections,
    setSharedCollections,
    sharedCollectionsGames,
    setSharedCollectionsGames,
    getTabCollections,
    collectionName,
    setCollectionName,
    isEditingCollectionName,
    setIsEditingCollectionName,
    userSearchQuery,
    setUserSearchQuery,
    userSearchResults,
    setUserSearchResults,
    showUserDropdown,
    setShowUserDropdown,
    selectedUsers,
    setSelectedUsers,
    sharedUsersList,
    setSharedUsersList
  } = useCollectionSharing();

  const {
    featureSearchQuery,
    setFeatureSearchQuery,
    featureSearchResults,
    setFeatureSearchResults,
    selectedFeatures,
    setSelectedFeatures,
    showFeatureDropdown,
    setShowFeatureDropdown
  } = useCollectionFeatures();

  // Fetch full game details for preview
  const fetchGameDetails = async (game) => {
    if (!game) return;

    const gameId = game.id || game.game_id;
    if (!gameId) return;

    setLoadingPreview(true);
    try {
      const gameDetails = await gamesService.fetchGameDetails(gameId);
      setPreviewGame(gameDetails);
    } catch (err) {
      logger.error("Failed to fetch game details:", err);
      setPreviewGame(game);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Play List toggle handler
  const handleTogglePlayList = useCallback(async (game) => {
    const gameId = game.game_id || game.id;
    if (isInPlayList(gameId)) {
      await removeFromPlayList(gameId);
    } else {
      await addToPlayList(game);
      showSuccessBannerMessage(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'inherit', color: 'white' }}>
          Added to Play List <List size={16} />
        </span>
      );
    }
  }, [isInPlayList, addToPlayList, removeFromPlayList, showSuccessBannerMessage]);

  // Rating click handler
  const handleAddToCollection = useCallback(async (game) => {
    if (!user?.id) return;
    trackClick('collection_bucket_change', { action: 'open_add_modal', game_id: game.game_id || game.id, game_name: game.name });

    // Use existing data from the game
    setAddGameData({
      game_id: game.game_id || game.id,
      name: game.name,
      thumbnail: game.thumbnail,
      preference: game.preference !== null && game.preference !== undefined ? game.preference : null,
      ownership_status: game.ownership_status || 'no_shelf',
      has_played: game.has_played,
      isShared: game.isShared || false,
      additional_tags: game.additional_tags || []
    });
    setShowAddGameModal(true);
  }, [user, setAddGameData, setShowAddGameModal, trackClick]);

  // Game preview handlers using unified hook (handleLetSPlay comes from useLetsPlayModal)
  const {
    handleSearchSimilar,
    handleAddToMarketplace,
    handleAddReminder,
    handleCompare,
  } = useGamePreviewHandlers({
    setPreviewGame,
    setShowCurateSidebar,
    setMarketplaceGame,
    setReminderGame,
    curateBridge: { showSimilarGames, showComparison, resetCurate },
  });

  const handleOpenInExplore = useCallback((game) => {
    const search = game?.name ? `?search=${encodeURIComponent(game.name)}` : '';
    navigate(`/explore${search}`);
    setPreviewGame(null);
  }, [navigate, setPreviewGame]);

  // Handler for "To Be Played" pill toggle
  const handleToggleToBePlayedOnly = useCallback(() => {
    setShowToBePlayedOnly(prev => !prev);
  }, []);

  // Handler for insight CTA actions that modify collection controls
  const handleCollectionAction = useCallback((opts) => {
    if (!opts) return;
    // Reset all filters and sort to neutral state first
    setCollectionSearchQuery('');
    setFilterMode('all');
    setSortBy('name');
    setSortOrder('asc');
    setShowToBePlayedOnly(false);
    setSelectedFeatures([]);
    setShowEditControls(true);
    if (opts.type === 'to_be_played') {
      setShowToBePlayedOnly(true);
    } else if (opts.type === 'sort') {
      if (opts.sortBy) setSortBy(opts.sortBy);
      if (opts.sortOrder) setSortOrder(opts.sortOrder);
    }
    setTimeout(() => {
      const grid = document.querySelector('.collection-grid');
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, [setSortBy, setSortOrder, setCollectionSearchQuery, setFilterMode, setSelectedFeatures]);


  // Mobile responsive state (UI-only, not in hooks)
  const [showEditControls, setShowEditControls] = useState(() => {
    // Default to hidden on all platforms
    return false;
  });

  // User mapping for SessionForm (not in hooks - simple cache)
  const [userMap] = useState(new Map());

  // Pending changes tracking for collection edits
  const [pendingChangesCount, setPendingChangesCount] = useState(0);

  // Token incremented only on explicit refresh to decouple lastPlayedData
  // from optimistic collection mutations (add/remove/edit).
  const [lastPlayedRefreshToken, setLastPlayedRefreshToken] = useState(0);

  // Let's Play state - using shared hook
  const {
    showModal: showLetSPlayModal,
    selectedGame: letSPlayGame,
    showSessionForm: showSessionFormInLetSPlay,
    showRulesSummary: showRulesSummaryInLetSPlay,
    setShowSessionForm,
    handlers: letSPlayHandlers
  } = useLetsPlayModal(user, (_result) => {
    showSuccessBannerMessage("Session logged successfully!");
  });

  // ScorePad state for Collection
  const [showScorePadInLetSPlay, setShowScorePadInLetSPlay] = useState(false);
  const [scorePadSessionData, setScorePadSessionData] = useState(null);
  const [showShareWarning, setShowShareWarning] = useState(false);

  // Let's Play handlers - using shared hook
  const handleLetSPlay = letSPlayHandlers.openModal;
  const handleSeeRulesSummary = letSPlayHandlers.showRules;
  const handleLogSession = letSPlayHandlers.logSession;

  // ScorePad handlers
  const handleOpenScorePad = useCallback(() => {
    setShowScorePadInLetSPlay(true);
  }, []);

  const handleCloseScorePad = useCallback(() => {
    setShowScorePadInLetSPlay(false);
  }, []);

  const handleLogSessionFromScorePad = useCallback((data) => {
    setScorePadSessionData(data);
    setShowScorePadInLetSPlay(false);
    setShowSessionForm(true);
  }, [setShowSessionForm]);

  // Refs for click outside detection
  const featureDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (featureDropdownRef.current && !featureDropdownRef.current.contains(event.target)) {
        setShowFeatureDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowFeatureDropdown]);

  // Get current collection data based on active tab
  const currentCollectionData = useMemo(() => {
    logger.debug('[currentCollectionData] activeTab:', activeTab);
    if (activeTab === 'my-collection') {
      // Safety check: ensure collection is an array for user's own collection
      if (!Array.isArray(collection)) {
        logger.debug('[currentCollectionData] User collection is not an array:', collection);
        return [];
      }
      logger.debug('[currentCollectionData] Returning user collection:', collection.length, 'games');
      return collection.map(item => ({ ...item, isShared: false }));
    } else {
      // This is a shared collection tab - use sharedCollectionsGames
      const sharedTab = getTabCollections().find(tab => tab.id === activeTab);
      logger.debug('[currentCollectionData] Shared tab lookup:', sharedTab);
      if (sharedTab && sharedTab.shareUrl) {
        const games = sharedCollectionsGames[sharedTab.shareUrl] || [];
        logger.debug(`[currentCollectionData] Getting games for shared tab ${activeTab} (shareUrl: ${sharedTab.shareUrl}): ${games.length} games`);
        logger.debug('[currentCollectionData] All sharedCollectionsGames keys:', Object.keys(sharedCollectionsGames));
        return games.map(item => ({
          ...item,
          isShared: true,
          ownership_status: item.ownership_status || 'no_shelf'
        }));
      } else {
        logger.warn('[currentCollectionData] No shared tab found or no shareUrl for tab:', activeTab);
      }
    }
    return [];
  }, [activeTab, collection, sharedCollectionsGames, getTabCollections]);

  const filteredCollection = useMemo(() => {
    const filtered = currentCollectionData.filter(game => {
      const query = collectionSearchQuery && typeof collectionSearchQuery === 'string' ? collectionSearchQuery.toLowerCase() : '';

      // "To Be Played" filter takes precedence - shows owned games without preferences
      if (showToBePlayedOnly) {
        const status = (game.ownership_status || '').toLowerCase();
        const hasPreference = game.preference !== null &&
                               game.preference !== undefined &&
                               game.preference !== '';
        // Only show owned games without preferences
        if (status !== 'owned' && !game.isShared) {
          return false;
        }
        if (hasPreference) {
          return false; // Exclude games that already have preferences
        }
      } else {
        // Normal filter logic when "To Be Played" is not active
        if (filterMode === 'owned') {
          const status = (game.ownership_status || '').toLowerCase();
          if (status !== 'owned' && !game.isShared) {
            return false;
          }
        } else if (filterMode === 'interested') {
          const status = (game.ownership_status || '').toLowerCase();
          if (status !== 'interested' && !game.isShared) {
            return false;
          }
        } else if (filterMode === 'excluded') {
          const status = (game.ownership_status || '').toLowerCase();
          if (status !== 'excluded' && !game.isShared) {
            return false;
          }
        }
        // 'all' mode shows everything (no filtering)
      }

      // Filter by selected features - game must have ALL selected features
      if (selectedFeatures.length > 0) {
        const hasAllFeatures = selectedFeatures.every(feature => {
          const featureValue = feature.value && typeof feature.value === 'string' ? feature.value.toLowerCase() : '';
          const featureType = feature.type;

          if (featureType === 'mechanics') {
            return game.mechanics && Array.isArray(game.mechanics) && game.mechanics.some(m => m && typeof m === 'string' && m.toLowerCase() === featureValue);
          } else if (featureType === 'categories') {
            return game.categories && Array.isArray(game.categories) && game.categories.some(c => c && typeof c === 'string' && c.toLowerCase() === featureValue);
          } else if (featureType === 'families') {
            return game.subcategories && Array.isArray(game.subcategories) && game.subcategories.some(s => s && typeof s === 'string' && s.toLowerCase() === featureValue);
          } else if (featureType === 'designers') {
            return game.designers && Array.isArray(game.designers) && game.designers.some(d => d && typeof d === 'string' && d.toLowerCase() === featureValue);
          }
          return false;
        });

        if (!hasAllFeatures) {
          return false;
        }
      }

      // If no search query, show all games that passed previous filters
      if (!query) {
        return true;
      }

      // Then apply search filters
      // Search by name
      if (game.name && typeof game.name === 'string' && game.name.toLowerCase().includes(query)) {
        return true;
      }

      // Search by categories
      if (game.categories && Array.isArray(game.categories) && game.categories.some(cat => cat && typeof cat === 'string' && cat.toLowerCase().includes(query))) {
        return true;
      }

      // Search by mechanics
      if (game.mechanics && Array.isArray(game.mechanics) && game.mechanics.some(mech => mech && typeof mech === 'string' && mech.toLowerCase().includes(query))) {
        return true;
      }

      // Search by themes (subcategories)
      if (game.subcategories && Array.isArray(game.subcategories) && game.subcategories.some(theme => theme && typeof theme === 'string' && theme.toLowerCase().includes(query))) {
        return true;
      }

      // Search by designers
      if (game.designers && Array.isArray(game.designers) && game.designers.some(designer => designer && typeof designer === 'string' && designer.toLowerCase().includes(query))) {
        return true;
      }

      // Search by publishers
      if (game.publishers && Array.isArray(game.publishers) && game.publishers.some(publisher => publisher && typeof publisher === 'string' && publisher.toLowerCase().includes(query))) {
        return true;
      }

      // Search by description
      if (game.description && typeof game.description === 'string' && game.description.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'year_published':
          aValue = a.year_published || 0;
          bValue = b.year_published || 0;
          break;
        case 'preference':
          // Custom sort order: like_a_lot > like > neutral > dislike > null
          const getPreferenceValue = (pref) => {
            if (pref === 'like_a_lot') return 4;
            if (pref === 'like') return 3;
            if (pref === 'neutral') return 2;
            if (pref === 'dislike') return 1;
            return 0; // null or undefined
          };
          aValue = getPreferenceValue(a.preference);
          bValue = getPreferenceValue(b.preference);
          break;
        case 'last_played':
          const gameIdA = a.game_id || a.id;
          const gameIdB = b.game_id || b.id;
          const lastPlayedA = lastPlayedData[gameIdA];
          const lastPlayedB = lastPlayedData[gameIdB];

          // Assign values for played games, or sentinel values for unplayed
          // Unplayed games get Infinity in asc mode (sink to bottom) or -Infinity in desc mode (sink to bottom)
          aValue = lastPlayedA?.last_played ? new Date(lastPlayedA.last_played).getTime() : (sortOrder === 'asc' ? Infinity : -Infinity);
          bValue = lastPlayedB?.last_played ? new Date(lastPlayedB.last_played).getTime() : (sortOrder === 'asc' ? Infinity : -Infinity);
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric comparison
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [currentCollectionData, collectionSearchQuery, filterMode, selectedFeatures, sortBy, sortOrder, lastPlayedData, showToBePlayedOnly]);

  // Infinite scroll functions
  const loadMoreItems = useCallback(() => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);

    // Simulate loading delay for better UX
    setTimeout(() => {
      const currentLength = displayedItems.length;
      const nextItems = filteredCollection.slice(currentLength, currentLength + itemsPerLoad);

      if (nextItems.length === 0) {
        setHasMore(false);
      } else {
        setDisplayedItems(prev => [...prev, ...nextItems]);
      }

      setIsLoadingMore(false);
    }, 300);
  }, [setIsLoadingMore, setDisplayedItems, setHasMore, filteredCollection, itemsPerLoad, hasMore, isLoadingMore, displayedItems]);

  // Reset displayed items when filters change
  useEffect(() => {
    const initialItems = filteredCollection.slice(0, itemsPerLoad);
    setDisplayedItems(initialItems);
    setHasMore(filteredCollection.length > itemsPerLoad);
  }, [setDisplayedItems, setHasMore, filteredCollection, itemsPerLoad]);

  // Handle scroll event for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || isLoadingMore) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      // Use clientHeight for stable viewport on mobile (URL bar show/hide affects innerHeight)
      const windowHeight = document.documentElement.clientHeight || window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Load more when user is within 500px of bottom
      const scrollThreshold = 500;
      const shouldLoad = scrollTop + windowHeight >= documentHeight - scrollThreshold;

      if (shouldLoad) {
        loadMoreItems();
      }
    };

    // Add throttling to prevent too many calls
    let timeoutId;
    const throttledHandleScroll = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null;
      }, 100);
    };

    window.addEventListener('scroll', throttledHandleScroll);
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loadMoreItems, hasMore, isLoadingMore]);

  // Load collection data
  const loadCollection = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      const res = await httpRequest(`${API_BASE}/profile/collection`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setCollection(data);
        } else {
          logger.error("Collection API returned non-array:", data);
          setCollection([]);
        }
      }
    } catch (err) {
      logger.error("Failed to load collection:", err);
    } finally {
      setLoading(false);
    }
  }, [user, setCollection, setLoading]);

  // Refresh collection and last-played data in parallel, then reset pending count
  const handleRefreshCollection = useCallback(async () => {
    await Promise.all([
      loadCollection(),
      // Bump token so the lastPlayed effect fires after the parallel fetch resolves
    ]);
    setLastPlayedRefreshToken(t => t + 1);
    setPendingChangesCount(0);
  }, [loadCollection]);

  const loadSharedCollections = useCallback(async () => {
    if (!user?.id) {
      logger.warn("[loadSharedCollections] Cannot load shared collections: No user ID available");
      return;
    }
    logger.debug('[loadSharedCollections] Loading shared collections for user:', user.id);
    try {
      const res = await httpRequest(`${API_BASE}/collections/shared`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      logger.debug('[loadSharedCollections] Response status:', res.status);
      if (res.ok) {
        const data = await res.json();
        logger.debug('[loadSharedCollections] Loaded shared collections:', data?.length || 0, data);
        setSharedCollections(data);
      } else {
        logger.warn('[loadSharedCollections] Failed to load shared collections:', res.status);
        const errorText = await res.text();
        logger.warn('[loadSharedCollections] Error response:', errorText);
        setSharedCollections([]);
      }
    } catch (err) {
      logger.warn('[loadSharedCollections] Failed to load shared collections:', err.message);
      setSharedCollections([]);
    } finally {
      setSharedCollectionsLoaded(true);
    }
  }, [user, setSharedCollections]);

  // Load last played data using the dedicated lightweight endpoint.
  // Does NOT depend on `collection` to avoid re-firing on every optimistic mutation.
  const loadLastPlayedData = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await httpRequest(`${API_BASE}/profile/collection/last-played`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        // Backend returns { game_id: { last_played, sessionCount }, ... }
        // Keys are integers from Python; normalise to numbers for consistent lookups.
        const normalised = {};
        Object.entries(data).forEach(([key, val]) => {
          normalised[Number(key)] = val;
        });
        setLastPlayedData(normalised);
      } else {
        setLastPlayedData({});
      }
    } catch (error) {
      setLastPlayedData({});
    }
  }, [setLastPlayedData, user?.id]);

  // Fire loadLastPlayedData only on explicit refresh (via lastPlayedRefreshToken)
  // or on initial mount — NOT on every optimistic collection state mutation.
  useEffect(() => {
    if (user?.id) {
      loadLastPlayedData();
    }
  }, [user?.id, lastPlayedRefreshToken, loadLastPlayedData]);

  // BGG Import functionality moved to ProfileSidebar

  const excludeFromCollection = async (gameId) => {
    trackClick('collection_bucket_change', { action: 'exclude', game_id: gameId });
    const previousCollection = collection;
    // Optimistic update - mark as excluded in local state
    setCollection(prev => prev.map(g => {
      if ((g.game_id || g.id) === gameId) {
        return { ...g, ownership_status: 'excluded' };
      }
      return g;
    }));

    try {
      const res = await httpRequest(`${API_BASE}/profile/collection`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ game_id: gameId, ownership_status: "excluded" }),
      });
      if (res.ok) {
        showNotification("Game marked as Not for me", "success");
      } else {
        setCollection(previousCollection);
        showNotification("Failed to mark game as Not for me", "error");
      }
    } catch (err) {
      setCollection(previousCollection);
      logger.error("Exclude game error:", err);
      showNotification("Failed to mark game as Not for me", "error");
    }
  };

  const removeFromCollection = async (gameId) => {
    const game =
      currentCollectionData.find((g) => (g.game_id || g.id) === gameId) ||
      collection.find((g) => (g.game_id || g.id) === gameId);

    openConfirmDialog(
      "Are you sure you want to remove this game from your collection?",
      async () => {
        trackClick('collection_bucket_change', { action: 'remove', game_id: gameId });
        // Optimistic removal - remove from state immediately for smooth UI
        const previousCollection = collection;
        setCollection(prev => prev.filter(g => (g.game_id || g.id) !== gameId));

        try {
          const res = await httpRequest(`${API_BASE}/profile/collection/${gameId}`, {
            method: "DELETE",
            headers: authService.getAuthHeaders(),
          });
          if (res.ok) {
            showNotification("Game removed from collection", "success");
            // No need to reload - state is already updated
          } else {
            // Restore on failure
            setCollection(previousCollection);
            showNotification("Failed to remove game", "error");
          }
        } catch (err) {
          // Restore on error
          setCollection(previousCollection);
          logger.error("Remove game error:", err);
          showNotification("Failed to remove game", "error");
        }
      },
      async () => {
        await excludeFromCollection(gameId);
      },
      () => {
        if (!game) return;
        setAddGameData({
          game_id: game.game_id || game.id,
          name: game.name,
          thumbnail: game.thumbnail,
          preference: game.preference !== null && game.preference !== undefined ? game.preference : null,
          ownership_status: game.ownership_status || 'no_shelf',
          has_played: game.has_played,
          isShared: game.isShared || false,
          additional_tags: game.additional_tags || []
        });
        setShowAddGameModal(true);
      }
    );
  };

  // Handle collection name save
  const handleSaveCollectionName = () => {
    setIsEditingCollectionName(false);
    showNotification("Library name updated", "success");
  };

  // Handle immediate input change
  const handleFeatureInputChange = (query) => {
    setFeatureSearchQuery(query);
    debouncedFeatureSearch(query);
  };

  // Debounced version of feature search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFeatureSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setFeatureSearchResults([]);
        setShowFeatureDropdown(false);
        return;
      }

      try {
        const res = await httpRequest(
          `${API_BASE}/games/search?q=${encodeURIComponent(query)}&limit=0&include_features=true&feature_limit=10`,
          {
            method: "GET",
            headers: authService.getAuthHeaders(),
          }
        );
        if (res.ok) {
          const data = await res.json();
          // Filter to show mechanics, categories, families, and designers
          const relevantFeatures = (data.features || []).filter(
            f => f.type === 'mechanics' || f.type === 'categories' || f.type === 'families' || f.type === 'designers'
          );
          setFeatureSearchResults(relevantFeatures);
          setShowFeatureDropdown(relevantFeatures.length > 0);
        }
      } catch (err) {
        logger.error("Failed to search features:", err);
      }
    }, 300),
    [httpRequest, authService]
  );

  // Remove selected feature
  const handleRemoveFeature = useCallback((featureType, featureValue) => {
    setSelectedFeatures(prev =>
      prev.filter(f => !(f.type === featureType && f.value === featureValue))
    );
  }, [setSelectedFeatures]);

  const handleLaunchSessionFromAddGame = useCallback((savedGame) => {
    if (!savedGame) return;
    letSPlayHandlers.openModal(savedGame);
    letSPlayHandlers.logSession();
  }, [letSPlayHandlers]);

  // Handle game save from AddGameModal - updates local collection state
  const handleSaveGame = useCallback((savedGame) => {
    trackClick('followup_action', { action: 'shortlist_save', game_id: savedGame.game_id, game_name: savedGame.name });
    // Check if this is an edit (game already exists) or new addition
    const existingGameIndex = Array.isArray(collection) ? collection.findIndex(
      (game) => (game.game_id || game.id) === savedGame.game_id
    ) : -1;

    if (existingGameIndex !== -1) {
      // This is an edit - update existing game in place
      setCollection((prev) => {
        const updatedCollection = [...prev];
        updatedCollection[existingGameIndex] = savedGame;
        return updatedCollection;
      });
      // Increment pending changes count for edits
      setPendingChangesCount((prev) => prev + 1);
      showNotification("Library updated", "success");
    } else {
      // This is a new addition - prepend to collection
      setCollection((prev) => [savedGame, ...prev]);
      showNotification("Library updated", "success");
    }
  }, [collection, setCollection, setPendingChangesCount, showNotification, trackClick]);

  const handleAddFeature = (type, value) => {
    setSelectedFeatures((prev) => {
      const exists = prev.some((f) => f.type === type && f.value === value);
      if (exists) return prev;
      return [...prev, { type, value }];
    });
    setFeatureSearchQuery("");
    setShowFeatureDropdown(false);
  };

  // Load shared collection games
  const loadSharedCollectionGames = useCallback(async (shareUrl) => {
    if (!shareUrl) {
      logger.error('loadSharedCollectionGames called with empty shareUrl');
      return;
    }
    logger.debug(`Loading shared collection games for: ${shareUrl}`);
    try {
      const res = await httpRequest(`${API_BASE}/collections/shared/${shareUrl}/games`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        logger.debug(`Loaded ${data?.games?.length || 0} games for ${shareUrl}`, data);
        // Store games by shareUrl - backend returns {games: [...], total, ...}
        setSharedCollectionsGames(prev => ({
          ...prev,
          [shareUrl]: data?.games || []
        }));
      } else {
        logger.error('Failed to load shared collection games:', res.status);
        const errorText = await res.text();
        logger.error('Error response:', errorText);
      }
    } catch (err) {
      logger.error(`Failed to load shared collection ${shareUrl} games:`, err);
    }
  }, [setSharedCollectionsGames]);


  // Share collection functions
  const handleCreateSharedCollection = useCallback(async () => {
    if (!user?.id) return;

    try {
      const res = await httpRequest(`${API_BASE}/collections/shared`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${collectionName}'s Library`,
          is_public: false
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setShareUrl(data.share_url);
        // Check if this is a new collection (created recently) or existing one
        const isNewCollection = data.created_at && (Date.now() - new Date(data.created_at).getTime()) < 5000;
        const message = isNewCollection ?
          "Shared collection created!" :
          "Using existing shared collection!";
        showNotification(message, "success");
      } else {
        const errorData = await res.json().catch(() => ({}));
        showNotification(errorData.message || "Failed to get shared collection", "error");
      }
    } catch (err) {
      logger.error("Failed to get shared collection:", err);
      showNotification("Network error. Please try again.", "error");
    }
  }, [setShareUrl, user, collectionName, showNotification]);

  // Load shared users list
  const loadSharedUsersList = useCallback(async () => {
    if (!shareUrl) return;

    try {
      const res = await httpRequest(`${API_BASE}/collections/shared/${shareUrl}/shares`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        setSharedUsersList(data.shared_users || []);
      } else {
        logger.error('Failed to load shared users list:', res.status);
      }
    } catch (err) {
      logger.error("Failed to load shared users list:", err);
    }
  }, [setSharedUsersList, shareUrl]);

  // Remove user's access to shared collection
  const handleRemoveSharedUser = useCallback(async (userId, username) => {
    if (!shareUrl) return;

    const confirmed = window.confirm(`Are you sure you want to remove ${username}'s access to your collection?`);
    if (!confirmed) return;

    try {
      const res = await httpRequest(`${API_BASE}/collections/shared/${shareUrl}/shares/${userId}`, {
        method: "DELETE",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        showNotification(`Removed ${username}'s access to collection`, "success");
        // Refresh shared users list
        await loadSharedUsersList();
      } else {
        showNotification("Failed to remove user access", "error");
      }
    } catch (err) {
      logger.error("Failed to remove user access:", err);
      showNotification("Failed to remove user access", "error");
    }
  }, [shareUrl, loadSharedUsersList, showNotification]);

  // Initialize share collection on modal open
  const handleShareModalOpen = useCallback(async () => {
    setShowShareModal(true);
    if (!shareUrl) {
      await handleCreateSharedCollection();
    }
    // Load shared users list
    await loadSharedUsersList();
  }, [setShowShareModal, shareUrl, handleCreateSharedCollection, loadSharedUsersList]);

  const handleUserSearch = useCallback(async (query) => {
    setUserSearchQuery(query);
    if (query.length < 2) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      const res = await httpRequest(`${API_BASE}/auth/users/search?q=${encodeURIComponent(query)}&limit=10`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        const users = Array.isArray(data) ? data : (data.users || []);
        setUserSearchResults(users);
        setShowUserDropdown(users.length > 0);
      } else {
        setUserSearchResults([]);
        setShowUserDropdown(false);
      }
    } catch (err) {
      logger.error("Failed to search users:", err);
      setUserSearchResults([]);
      setShowUserDropdown(false);
    }
  }, [setShowUserDropdown, setUserSearchQuery, setUserSearchResults]);

  const handleAddUser = useCallback((user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers(prev => [...prev, user]);
    }
    setUserSearchQuery("");
    setUserSearchResults([]);
    setShowUserDropdown(false);
  }, [setSelectedUsers, selectedUsers, setShowUserDropdown, setUserSearchQuery, setUserSearchResults]);

  const handleRemoveUser = useCallback((userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  }, [setSelectedUsers, selectedUsers]);

  const handleShareWithUsers = useCallback(async () => {
    if (!shareUrl || selectedUsers.length === 0) return;

    try {
      for (const user of selectedUsers) {
        const res = await httpRequest(`${API_BASE}/collections/shared/${shareUrl}/share`, {
          method: "POST",
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: user.username || user.email
          }),
        });

        if (res.ok) {
          // Create notification for the recipient
          await httpRequest(`${API_BASE}/notifications`, {
            method: "POST",
            headers: {
              ...authService.getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              title: "Library Shared",
              message: `Library was shared by ${user?.username || 'a user'}`,
              user_id: user.id
            }),
          });
        }
      }

      showNotification(`Collection shared with ${selectedUsers.length} user(s)!`, "success");
      setSelectedUsers([]);
    } catch (err) {
      logger.error("Failed to share collection:", err);
      showNotification("Failed to share collection", "error");
    }
  }, [setSelectedUsers, shareUrl, selectedUsers, showNotification]);

  const handleSendNotificationToUser = useCallback(async (targetUser) => {
    if (!shareUrl) return;

    try {
      const res = await httpRequest(`${API_BASE}/collections/shared/${shareUrl}/share`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: targetUser.username || targetUser.email
        }),
      });

      if (res.ok) {
        // Create notification for the recipient
        await httpRequest(`${API_BASE}/notifications`, {
          method: "POST",
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: "Library Shared",
            message: `Library was shared by ${user?.username || 'a user'}`,
            user_id: targetUser.id
          }),
        });

        showNotification(`Collection shared with ${targetUser.username || targetUser.email}!`, "success");

        // Refresh shared users list
        await loadSharedUsersList();

        // Remove from selected users
        setSelectedUsers(prev => prev.filter(u => u.id !== targetUser.id));
      } else {
        showNotification(`Failed to share with ${targetUser.username || targetUser.email}`, "error");
      }
    } catch (err) {
      logger.error("Failed to share collection:", err);
      showNotification("Failed to share collection", "error");
    }
  }, [user, loadSharedUsersList, shareUrl, setSelectedUsers, showNotification]);

  const handleShareCollection = useCallback(async () => {
    if (!shareUrl) return;

    const fullUrl = `${window.location.origin}/collection?shared=${shareUrl}`;
    const shareText = `Let's play! Here's my collection, join me on Pista: ${collectionName}\n${fullUrl}`;

    // Show Firefox warning if applicable
    if (isFirefox()) {
      setShowShareWarning(true);
    }

    // Check if we're in a mobile app environment
    const isNativeMobile = window.Capacitor?.isNativePlatform();

    // Try Capacitor Share plugin first for mobile apps
    if (isNativeMobile && window.Capacitor?.Plugins?.Share) {
      try {
        await window.Capacitor.Plugins.Share.share({
          title: `${collectionName}'s Library`,
          text: shareText,
          url: fullUrl
        });
        showNotification("Library shared!", "success");
        return;
      } catch (err) {
        logger.error("Capacitor Share failed:", err);
        if (err.name === 'AbortError') {
          showNotification("Share cancelled", "info");
          return;
        }
        // Fall back to Web Share API
      }
    }

    // Try Web Share API (works on mobile browsers)
    if (navigator?.share && !isNativeMobile) {
      try {
        await navigator.share({
          title: `${collectionName}'s Library`,
          text: shareText,
          url: fullUrl
        });
        showNotification("Library shared!", "success");
        return;
      } catch (err) {
        logger.error("Web Share API failed:", err);
        if (err.name === 'AbortError') {
          showNotification("Share cancelled", "info");
          return;
        }
        // Fall back to clipboard
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(fullUrl);
      showNotification("Share link copied to clipboard!", "success");
    } catch (err) {
      showNotification("Failed to share collection", "error");
    }
  }, [collectionName, shareUrl, showNotification]);

  // Initialize data
  useEffect(() => {
    logger.debug('[Init] Starting data initialization for user:', user?.username, 'ID:', user?.id);
    if (user?.username) {
      setCollectionName(user.username);
    }
    loadCollection();
    loadSharedCollections().then(() => {
      logger.debug('[Init] Shared collections loaded');
    });

    // Handle URL parameter for shared collection
    const urlParams = new URLSearchParams(window.location.search);
    const sharedParam = urlParams.get('shared');
    logger.debug('[Init] URL shared param:', sharedParam);
    
    // The separate [sharedCollections] effect below handles auto-selection
    // once sharedCollections arrive — no setTimeout needed.
  }, [setCollection, setLoading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track whether a join attempt has already been made to avoid repeated calls
  const joinAttemptedRef = useRef(false);

  // Separate effect to handle auto-selection when sharedCollections changes
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedParam = urlParams.get('shared');
    
    if (!sharedParam || !user?.id || !sharedCollectionsLoaded) return;

    logger.debug('[AutoSelect] Checking for shared collection:', sharedParam);
    logger.debug('[AutoSelect] Available shared collections:', sharedCollections.map(sc => ({ id: sc.id, name: sc.name, share_url: sc.share_url })));
    
    // Find the shared collection by share_url
    const sharedCollection = sharedCollections.find(sc => sc.share_url === sharedParam);
    logger.debug('[AutoSelect] Found shared collection:', sharedCollection);
    
    if (sharedCollection) {
      const tabId = `shared-${sharedCollection.id}`;
      logger.debug('[AutoSelect] Setting active tab:', tabId);
      setActiveTab(tabId);
      initializeTabData(tabId);
      loadSharedCollectionGames(sharedCollection.share_url);
    } else if (!joinAttemptedRef.current) {
      // Collection not in user's list — attempt to join via link
      joinAttemptedRef.current = true;
      logger.debug('[AutoSelect] Collection not found in list, attempting to join:', sharedParam);
      
      (async () => {
        try {
          const res = await httpRequest(`${API_BASE}/collections/shared/${encodeURIComponent(sharedParam)}/join`, {
            method: "POST",
            headers: {
              ...authService.getAuthHeaders(),
              'Content-Type': 'application/json'
            },
          });

          if (res.ok) {
            logger.debug('[AutoSelect] Successfully joined collection, reloading shared collections');
            await loadSharedCollections();
          } else {
            const errorData = await res.json().catch(() => ({}));
            logger.warn('[AutoSelect] Failed to join collection:', res.status, errorData.detail);
            showNotification(errorData.detail || "Could not join shared collection", "error");
          }
        } catch (err) {
          logger.error('[AutoSelect] Error joining shared collection:', err);
          showNotification("Failed to join shared collection", "error");
        }
      })();
    }
  }, [sharedCollections, user?.id, sharedCollectionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="profile-container collection-page-container" style={{
        minHeight: "100vh"
      }}>
      {/* Collections Content - No Tabs */}
      <div className="tab-content-wrapper" style={{
        width: "100vw",
        marginLeft: "calc(-50vw + 50%)",
        marginRight: "calc(-50vw + 50%)"
      }}>
        <div className="collections-tab-content">
            <div className="page-insights-band">
              <div className="page-insights-band__inner">
                <InsightCarousel
                  compact={true}
                  view="collection"
                  user={user}
                  onOpenCurate={(message) => {
                    setShowCurateSidebar(true);
                    setTimeout(() => {
                      if (message) setInput(message);
                    }, 100);
                  }}
                  onShowSimilar={(game) => {
                    setShowCurateSidebar(true);
                    setTimeout(() => showSimilarGames(game), 100);
                  }}
                  onLetsPlay={(game) => handleLetSPlay(game)}
                  onAddToPlayList={addToPlayList}
                  onCollectionAction={handleCollectionAction}
                />
              </div>
            </div>

            <div className="profile-section">
              {/* Collection Tabs */}
              <CollectionTabs
                tabs={getTabCollections()}
                activeTab={activeTab}
                onTabChange={(tabId) => {
                  // Prevent redundant tab changes that could cause infinite loops
                  if (tabId !== activeTab) {
                    logger.debug(`Tab changed to: ${tabId}`);
                    setActiveTab(tabId);
                    // Initialize tab data and load games if this is a shared collection tab
                    if (tabId !== 'my-collection') {
                      initializeTabData(tabId);
                      const sharedTab = getTabCollections().find(tab => tab.id === tabId);
                      logger.debug(`Found shared tab:`, sharedTab);
                      if (sharedTab?.shareUrl) {
                        logger.debug(`Loading shared collection games for tab ${tabId}, shareUrl: ${sharedTab.shareUrl}`);
                        loadSharedCollectionGames(sharedTab.shareUrl);
                      } else {
                        logger.warn(`No shareUrl found for tab ${tabId}`);
                      }
                    }
                  }
                }}
                tabCounts={{
                  'my-collection': Array.isArray(collection) ? collection.length : 0,
                  ...Object.fromEntries(
                    getTabCollections()
                      .filter(tab => tab.type === 'shared')
                      .map(tab => [
                        tab.id,
                        sharedCollectionsGames[tab.shareUrl]?.length || 0
                      ])
                  )
                }}
                user={user}
                playListCount={playListCount}
                onOpenPlayList={() => setShowPlayListSidebar(true)}
              />

              {/* Collection Header - Only show for active tab */}
              {activeTab === 'my-collection' && (
                <CollectionHeader
                  // Collection name state (from CollectionNameRow)
                  collectionName={collectionName}
                  setCollectionName={setCollectionName}
                  filteredCollectionCount={filteredCollection.length}
                  isEditingCollectionName={isEditingCollectionName}
                  setIsEditingCollectionName={setIsEditingCollectionName}
                  onSaveCollectionName={handleSaveCollectionName}
                  user={user}
                  // Actions (from both components)
                  onShareModalOpen={handleShareModalOpen}
                  onToggleEditControls={() => setShowEditControls(!showEditControls)}
                  showEditControls={showEditControls}
                  onAddGame={() => {
                    setAddGameData(null);
                    setShowAddGameModal(true);
                  }}
                  // Filter mode for ownership switch
                  filterMode={filterMode}
                  onFilterModeChange={setFilterMode}
                  // Pending changes and refresh
                  pendingChangesCount={pendingChangesCount}
                  onRefreshCollection={handleRefreshCollection}
                />
              )}

              {/* Combined Controls - Edit and Search */}
              <CollectionControls
                showEditControls={showEditControls}
                collectionSearchQuery={collectionSearchQuery}
                onSearchQueryChange={setCollectionSearchQuery}
                featureDropdownRef={featureDropdownRef}
                featureSearchQuery={featureSearchQuery}
                onFeatureSearchChange={handleFeatureInputChange}
                showFeatureDropdown={showFeatureDropdown}
                featureSearchResults={featureSearchResults}
                onAddFeature={handleAddFeature}
                selectedFeatures={selectedFeatures}
                onRemoveFeature={handleRemoveFeature}
                sortBy={sortBy}
                onSortByChange={setSortBy}
                sortOrder={sortOrder}
                onToggleSortOrder={toggleSortOrder}
                // "To Be Played" pill
                showToBePlayedOnly={showToBePlayedOnly}
                onToggleToBePlayedOnly={handleToggleToBePlayedOnly}
              />


              {/* Collection Display */}
              {activeTab === 'my-collection' ? (
                // User's own collection
                loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading collection...</p>
                  </div>
                ) : displayedItems.length > 0 ? (
                  <React.Fragment key="collection-grid">
                    {/* Collection Grid */}
                    <div className="collection-grid">
                      {displayedItems.map((game) => {
                        const gameId = game.game_id || game.id;
                        const lastPlayed = lastPlayedData[gameId];

                        return (
                          <CollectionTileWithBulkOnline
                            key={`game-${game.id}-${game.game_id || ""}`}
                            game={game}
                            getGameAvailability={getGameAvailability}
                            lastPlayed={lastPlayed}
                            onClick={fetchGameDetails}
                            onRemove={removeFromCollection}
                            onEdit={handleAddToCollection}
                            onRatingClick={handleAddToCollection}
                            user={user}
                            onTogglePlayList={handleTogglePlayList}
                            inPlayList={isInPlayList(gameId)}
                          />
                        );
                      })}
                    </div>

                    {/* Load More Button - Outside grid, centered */}
                    {hasMore && !isLoadingMore && (
                      <div style={{ textAlign: "center", padding: "2rem" }}>
                        <button
                          onClick={loadMoreItems}
                          style={{
                            padding: "0.75rem 1.5rem",
                            backgroundColor: "var(--pista-green-primary, #2E7D32)",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "1rem",
                            fontWeight: "600"
                          }}
                        >
                          Load More Games
                        </button>
                      </div>
                    )}

                    {/* Infinite Scroll Loading Indicator */}
                    {isLoadingMore && (
                      <div style={{ textAlign: "center", padding: "2rem" }}>
                        <div className="loading-spinner"></div>
                        <p style={{ color: "var(--text-secondary, #666)" }}>Loading more games...</p>
                      </div>
                    )}

                    {/* End of Collection Indicator */}
                    {!hasMore && displayedItems.length > 0 && (
                      <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-secondary, #666)" }}>
                        <p>End of collection</p>
                      </div>
                    )}
                  </React.Fragment>
                ) : (
                  <CollectionEmptyState />
                )
              ) : (
                // Shared collection tab
                <SharedCollectionTab
                  sharedCollection={getTabCollections().find(tab => tab.id === activeTab)}
                  games={sharedCollectionsGames[getTabCollections().find(tab => tab.id === activeTab)?.shareUrl] || []}
                  loading={loading}
                  filteredCount={sharedCollectionsGames[getTabCollections().find(tab => tab.id === activeTab)?.shareUrl]?.length || 0}
                  showEditControls={showEditControls}
                  collectionSearchQuery={collectionSearchQuery}
                  onSearchQueryChange={setCollectionSearchQuery}
                  featureDropdownRef={featureDropdownRef}
                  featureSearchQuery={featureSearchQuery}
                  onFeatureSearchChange={handleFeatureInputChange}
                  showFeatureDropdown={showFeatureDropdown}
                  featureSearchResults={featureSearchResults}
                  onAddFeature={handleAddFeature}
                  selectedFeatures={selectedFeatures}
                  onRemoveFeature={handleRemoveFeature}
                  sortBy={sortBy}
                  onSortByChange={setSortBy}
                  sortOrder={sortOrder}
                  onToggleSortOrder={toggleSortOrder}
                  showToBePlayedOnly={showToBePlayedOnly}
                  onToggleToBePlayedOnly={handleToggleToBePlayedOnly}
                  filterMode={filterMode}
                  onFilterModeChange={setFilterMode}
                  onGameClick={fetchGameDetails}
                  onAddToCollection={handleAddToCollection}
                  onPreviewGame={setPreviewGame}
                  hasMore={hasMore}
                  onLoadMore={loadMoreItems}
                  itemsPerLoad={itemsPerLoad}
                />
              )}
            </div>
          </div>
        </div>

      {/* Notification Toast */}
      {notification.show && (
        <div
          className={`notification-toast notification-${notification.type}`}
          style={{
            position: 'fixed',
            top: 'calc(80px + env(safe-area-inset-top, 0px))', // Below the navigation
            left: '50%',
            transform: 'translateX(-50%)', // Center horizontally
            padding: '12px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '500',
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            maxWidth: '400px',
            wordWrap: 'break-word',
            backgroundColor: notification.type === 'success' ? '#4caf50' :
                           notification.type === 'error' ? '#f44336' :
                           notification.type === 'warning' ? '#ff9800' : '#2196f3',
            animation: 'slideInTop 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      {/* Add Game Modal */}
      <AddGameModal
        game={addGameData}
        isOpen={showAddGameModal}
        initialData={addGameData ? {
          preference: addGameData.preference,
          ownership_status: addGameData.ownership_status,
          has_played: addGameData.has_played,
          additional_tags: addGameData.additional_tags,
        } : {}}
        onLaunchSession={handleLaunchSessionFromAddGame}
        onClose={() => {
          setShowAddGameModal(false);
          setAddGameData({
            preference: null,
            ownership_status: "no_shelf",
            has_played: false,
            isShared: false,
            additional_tags: []
          });
        }}
        onSave={handleSaveGame}
        user={user}
        collection={collection}
      />

      {/* Game Preview Modal */}
      {previewGame && (
        <GamePreviewModal
          game={previewGame}
          showAddToQuery={false}
          onSearchSimilar={handleSearchSimilar}
          onOpenInExplore={handleOpenInExplore}
          onCompare={handleCompare}
          onAddToMarketplace={handleAddToMarketplace}
          onAddReminder={handleAddReminder}
          onLetSPlay={handleLetSPlay}
          letsPlayConfig={{
            onLogSession: (game) => {
              letSPlayHandlers.openModal(game);
              setShowScorePadInLetSPlay(false);
              setShowSessionForm(true);
            },
            onOpenScorePad: (game) => {
              letSPlayHandlers.openModal(game);
              setShowSessionForm(false);
              setShowScorePadInLetSPlay(true);
            },
          }}
          onClose={() => {
            setPreviewGame(null);
            setLoadingPreview(false);
          }}
          user={user}
          onTogglePlayList={handleTogglePlayList}
          inPlayList={isInPlayList(previewGame?.id || previewGame?.game_id)}
        />
      )}

      {/* Loading indicator for game preview */}
      {loadingPreview && (
        <div
          className="safe-area-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "var(--bg-primary, white)",
              padding: "2rem",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "1rem", color: "var(--text-primary, #333)" }}>
              Loading game details...
            </div>
          </div>
        </div>
      )}

      {/* Let's Play Modal */}
      <LetsPlayModal
        isOpen={showLetSPlayModal}
        game={letSPlayGame}
        user={user}
        onClose={() => {
          setShowScorePadInLetSPlay(false);
          setScorePadSessionData(null);
          letSPlayHandlers.closeModal();
        }}
        showSessionForm={showSessionFormInLetSPlay}
        showRulesSummary={showRulesSummaryInLetSPlay}
        showScorePad={showScorePadInLetSPlay}
        onLogSession={handleLogSession}
        onSeeRulesSummary={handleSeeRulesSummary}
        onOpenScorePad={handleOpenScorePad}
        onCloseScorePad={handleCloseScorePad}
        onLogSessionFromScorePad={handleLogSessionFromScorePad}
        scorePadSessionData={scorePadSessionData}
        onSaveSession={letSPlayHandlers.handleSessionLogged}
        showBanner={showTransientBanner}
        userMap={userMap}
        source="collection"
      />

      {/* Curate Sidebar — always mounted so ref stays valid; CurateSimplified owns its SidebarShell */}
      <CurateSimplified
        ref={setCurateRef}
        isOpen={showCurateSidebar}
        user={user}
        onClose={() => setShowCurateSidebar(false)}
        sidebarMode={true}
        getGameOwnershipStatus={getGameOwnershipStatusForGame}
      />

      {/* Play List Sidebar */}
      <PlayListSidebar
        isOpen={showPlayListSidebar}
        onClose={() => setShowPlayListSidebar(false)}
        playList={playList}
        loading={playListLoading}
        onRemove={removeFromPlayList}
        onClear={clearPlayList}
        onLaunchJetSetGo={() => navigate('/quick-decision')}
      />

      {/* Share Collection Modal */}
      {showShareModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary, white)',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80dvh',
            overflow: 'auto',
            border: '1px solid var(--border-color, #e0e0e0)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary, #333)', fontSize: '1.5rem', fontWeight: '600' }}>Share Library</h3>
              <button
                onClick={() => setShowShareModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-secondary, #666)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  lineHeight: '1'
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {!shareUrl ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ marginBottom: '1rem', color: 'var(--text-secondary, #666)' }}>Creating share link...</div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid var(--pista-orange-primary, #ff9800)',
                  borderTop: '3px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }}></div>
              </div>
            ) : (
              <>
                {/* Share Link Section */}
                <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: 'var(--bg-secondary, #f5f5f5)', borderRadius: '8px' }}>
                  {showShareWarning && (
                    <ShareWarningBanner 
                      message="Share functionality may be limited in Firefox. Contents copied to clipboard, ready to share."
                      className="collection-share-warning"
                    />
                  )}
                  <button
                    onClick={handleShareCollection}
                    style={{
                      backgroundColor: 'var(--pista-green-primary, #4caf50)',
                      color: 'var(--pista-button-text, white)',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      width: '100%'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#45a049';
                      e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'var(--pista-green-primary, #4caf50)';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    Share with Link
                  </button>
                </div>

                {/* Share with Users Section */}
                <div>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary, #333)', fontSize: '1.1rem', fontWeight: '600' }}>Share with Users</h4>

                  {/* User Search */}
                  <div style={{ marginBottom: '1rem', position: 'relative' }}>
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => handleUserSearch(e.target.value)}
                      placeholder="Search users by username..."
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-color, #ddd)',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        backgroundColor: 'var(--bg-primary, white)',
                        color: 'var(--text-primary, #333)'
                      }}
                    />

                    {/* User Search Dropdown */}
                    {showUserDropdown && userSearchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'var(--bg-primary, white)',
                        border: '1px solid var(--border-color, #ddd)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        marginTop: '0.5rem'
                      }}>
                        {userSearchResults.map(user => (
                          <div
                            key={user.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.75rem',
                              borderBottom: '1px solid var(--border-color, #eee)',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--bg-secondary, #f5f5f5)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            onClick={() => handleAddUser(user)}
                          >
                            <span style={{ color: 'var(--text-primary, #333)' }}>{user.username || user.email}</span>
                            <span style={{ color: 'var(--pista-green-primary, #4caf50)', fontSize: '0.8rem', fontWeight: '600' }}>+ Add</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected Users */}
                  {selectedUsers.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h5 style={{ marginBottom: '0.5rem', color: 'var(--text-primary, #333)', fontSize: '1rem', fontWeight: '600' }}>Selected Users:</h5>
                      {selectedUsers.map(user => (
                        <div
                          key={user.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem',
                            border: '1px solid var(--border-color, #ddd)',
                            borderRadius: '8px',
                            marginBottom: '0.5rem',
                            backgroundColor: 'var(--bg-secondary, #f5f5f5)'
                          }}
                        >
                          <span style={{ color: 'var(--text-primary, #333)' }}>{user.username || user.email}</span>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleSendNotificationToUser(user)}
                              style={{
                                backgroundColor: 'var(--pista-orange-primary, #ff9800)',
                                color: 'var(--pista-button-text, white)',
                                border: 'none',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = 'var(--pista-orange-dark, #f57c00)';
                                e.target.style.transform = 'scale(1.02)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'var(--pista-orange-primary, #ff9800)';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              {NOTIFICATION_ICON}
                            </button>
                            <button
                              onClick={() => handleRemoveUser(user.id)}
                              style={{
                                backgroundColor: 'var(--danger-color, #f44336)',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = 'var(--danger-color-dark, #d32f2f)';
                                e.target.style.transform = 'scale(1.02)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = '#f44336';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              -
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Share Button */}
                  {selectedUsers.length > 0 && (
                    <button
                      onClick={handleShareWithUsers}
                      style={{
                        backgroundColor: 'var(--primary-color, #1976d2)',
                        color: 'var(--pista-button-text, white)',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        width: '100%'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = 'var(--primary-color-dark, #1565c0)';
                        e.target.style.transform = 'scale(1.02)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'var(--primary-color, #1976d2)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      Share with {selectedUsers.length} User(s)
                    </button>
                  )}

                  {/* Already Shared With Section */}
                  {sharedUsersList.length > 0 && (
                    <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-tertiary, #f9f9f9)', borderRadius: '8px' }}>
                      <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary, #333)', fontSize: '1rem', fontWeight: '600' }}>
                        Already Shared With ({sharedUsersList.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {sharedUsersList.map(sharedUser => (
                          <div
                            key={sharedUser.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.75rem',
                              backgroundColor: 'white',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color, #ddd)'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: '500', color: 'var(--text-primary, #333)' }}>
                                {sharedUser.username}
                              </div>
                              {sharedUser.shared_at && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary, #999)' }}>
                                  Shared on {new Date(sharedUser.shared_at).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveSharedUser(sharedUser.id, sharedUser.username)}
                              style={{
                                backgroundColor: 'var(--danger-color, #f44336)',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = 'var(--danger-color-dark, #d32f2f)';
                                e.target.style.transform = 'scale(1.1)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'var(--danger-color, #f44336)';
                                e.target.style.transform = 'scale(1)';
                              }}
                              title={`Remove ${sharedUser.username}'s access`}
                            >
                              −
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.show && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null, onExclude: null, onChangeCategory: null })}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setConfirmDialog({ show: false, message: "", onConfirm: null, onExclude: null, onChangeCategory: null });
            }
          }}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-secondary, white)',
              padding: '2rem',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              maxWidth: '400px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary, #333)', fontSize: '1.25rem', fontWeight: 600 }}>
                Delete from Collection
              </h3>
              <button
                onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null, onExclude: null, onChangeCategory: null })}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-secondary, #666)',
                  lineHeight: 1,
                  padding: '0 0.25rem'
                }}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-primary, #333)' }}>{confirmDialog.message}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', backgroundColor: 'var(--bg-secondary, white)', padding: '0.75rem', borderRadius: '8px' }}>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {confirmDialog.onExclude ? (
                  <button
                    onClick={() => {
                      if (confirmDialog.onExclude) {
                        confirmDialog.onExclude();
                      }
                      setConfirmDialog({ show: false, message: "", onConfirm: null, onExclude: null, onChangeCategory: null });
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid var(--border-color, #ddd)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--bg-tertiary, #f5f5f5)',
                      color: 'var(--text-primary, #333)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      lineHeight: 1.3
                    }}
                  >
                    <span>Keep, but Exclude</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-secondary, #666)' }}>
                      from future recommendations
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null, onExclude: null, onChangeCategory: null })}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid var(--border-color, #ddd)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--bg-tertiary, #f5f5f5)',
                      color: 'var(--text-primary, #333)',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={() => {
                    if (confirmDialog.onConfirm) {
                      confirmDialog.onConfirm();
                    }
                    setConfirmDialog({ show: false, message: "", onConfirm: null, onExclude: null, onChangeCategory: null });
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: 'var(--danger-color, #dc3545)',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {confirmDialog.onExclude ? 'Delete' : 'Confirm'}
                </button>
              </div>
              {confirmDialog.onChangeCategory && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      if (confirmDialog.onChangeCategory) {
                        confirmDialog.onChangeCategory();
                      }
                      setConfirmDialog({ show: false, message: "", onConfirm: null, onExclude: null, onChangeCategory: null });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: 'var(--link-color, #1976d2)',
                      fontSize: '0.75rem',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    OR, change category
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Marketplace Modal */}
      {marketplaceGame && (
        <Marketplace
          gameId={marketplaceGame.id}
          gameName={marketplaceGame.name}
          onClose={() => setMarketplaceGame(null)}
        />
      )}

      {/* Reminder Form Modal */}
      {reminderGame && (
        <SimplifyReminderModal
          gameId={reminderGame.id}
          gameName={reminderGame.name}
          userCountry={user?.country_of_domicile}
          onClose={() => setReminderGame(null)}
          showSuccessBannerMessage={showSuccessBannerMessage}
          showTransientBanner={showTransientBanner}
        />
      )}

      {/* Transient banner for game selection */}
      {showGameSelectionBanner && (
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            backgroundColor: "var(--success-color, #4caf50)",
            color: "white",
            padding: "1rem",
            borderRadius: "4px",
            zIndex: 1000,
            maxWidth: "300px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            animation: "slideDown 0.3s ease-out"
          }}
        >
          {SUCCESS_ICON} {bannerMessage}
        </div>
      )}

      {/* Global Success Banner */}
      {showSuccessBanner && (
        <div
          style={{
            position: "fixed",
            top: "calc(20px + env(safe-area-inset-top, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            backgroundColor: "var(--success-color, #4caf50)",
            color: "white",
            padding: "1rem 2rem",
            borderRadius: "4px",
            zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            animation: "slideDown 0.3s ease-out"
          }}
        >
          {SUCCESS_ICON} {successBannerMessage}
        </div>
      )}
      </div>,
    </>
  );
}

// Export utility functions for ownership status checking
export const isGameOwned = (game) => {
  const status = (game.ownership_status || '').toLowerCase();
  return status === 'owned' || game.isShared;
};

export default Collection;
